import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  Loader,
  Pagination,
  Progress,
  ScrollArea,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCheck,
  IconPlayerPlay,
  IconSearch,
  IconSparkles,
} from "@tabler/icons-react";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, getApiErrorMessage } from "../../lib/api";
import type { CategoryTreeNode } from "../../types/category";
import { Th } from "../../components/Th";

// --- Types ---

interface ClassificationRun {
  id: string;
  status: "pending" | "running" | "completed" | "failed";
  stats: {
    total?: number;
    classified?: number;
    failed?: number;
  } | null;
  errorMessage: string | null;
  createdAt: string;
}

interface ClassifiedProduct {
  id: string;
  name: string;
  modelNumber: string | null;
  brandId: string;
  brandName: string;
  productLineId: string | null;
  productLineName: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryPath: string | null;
  classificationConfidence: number | null;
  classifiedAt: string | null;
  classificationMethod: string | null;
  attributes: Record<string, unknown> | null;
  msrp: number | null;
  imageThumbUrl: string | null;
}


type ConfidenceBand = "all" | "low" | "medium" | "high" | "unclassified";

// --- Helpers ---

function confidenceColor(c: number | null): string {
  if (c == null) return "gray";
  if (c < 0.7) return "red";
  if (c < 0.85) return "yellow";
  return "green";
}

function confidenceLabel(c: number | null): string {
  if (c == null) return "—";
  return `${(Number(c) * 100).toFixed(0)}%`;
}

function attrBadges(attrs: Record<string, unknown> | null): string[] {
  if (!attrs) return [];
  const display: string[] = [];
  if (attrs.screen_size_inches) display.push(`${attrs.screen_size_inches}"`);
  if (attrs.display_technology) display.push(String(attrs.display_technology));
  if (attrs.resolution) display.push(String(attrs.resolution));
  if (attrs.model_year) display.push(String(attrs.model_year));
  if (attrs.storage_capacity) display.push(String(attrs.storage_capacity));
  return display;
}

function formatPath(path: string | null): string {
  if (!path) return "Unclassified";
  return path.split(".").join(" > ");
}

// --- Component ---

export function ClassifyPage() {
  // Run status
  const [run, setRun] = useState<ClassificationRun | null>(null);
  const [runLoading, setRunLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Product list
  const [products, setProducts] = useState<ClassifiedProduct[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [confidenceBand, setConfidenceBand] = useState<ConfidenceBand>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [productsLoading, setProductsLoading] = useState(false);
  const [sortBy, setSortBy] = useState<string>("name");
  const [sortReversed, setSortReversed] = useState(false);

  // Categories for override
  const [categories, setCategories] = useState<CategoryTreeNode[]>([]);

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [approving, setApproving] = useState(false);

  // Detail drawer
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);
  const [selectedProduct, setSelectedProduct] = useState<ClassifiedProduct | null>(null);
  const [editL1, setEditL1] = useState<string | null>(null);
  const [editL2, setEditL2] = useState<string | null>(null);
  const [editL3, setEditL3] = useState<string | null>(null);
  const [editLineName, setEditLineName] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSort = (field: string) => {
    const reversed = field === sortBy ? !sortReversed : false;
    setSortReversed(reversed);
    setSortBy(field);
    setPage(1);
  };

  // Issue #8: Track consecutive poll failures to avoid infinite loop
  const pollFailCountRef = useRef(0);

  // Fetch latest run status
  const fetchRunStatus = useCallback(async () => {
    try {
      const res = await api.get<ClassificationRun | null>("/admin/classify/status");
      setRun(res.data ?? null);
      pollFailCountRef.current = 0;
    } catch (err) {
      pollFailCountRef.current += 1;
      if (pollFailCountRef.current >= 5) {
        if (pollRef.current) clearInterval(pollRef.current);
        notifications.show({ color: "red", message: "Lost connection to server. Status updates paused." });
      } else if (!axios.isAxiosError(err) || err.response?.status !== 404) {
        // Don't notify on 404 (no run yet) — only on real errors
        notifications.show({ color: "orange", message: "Failed to load classification status" });
      }
    } finally {
      setRunLoading(false);
    }
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const start = (page - 1) * pageSize;
      const res = await api.get<ClassifiedProduct[]>("/admin/classify/products", {
        params: {
          _start: start,
          _end: start + pageSize,
          _sort: sortBy,
          _order: sortReversed ? "DESC" : "ASC",
          confidence_band: confidenceBand,
          search: debouncedSearch || undefined,
        },
      });
      setProducts(res.data);
      const totalHeader = res.headers["x-total-count"];
      if (totalHeader) setTotal(Number(totalHeader));
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to load products") });
    } finally {
      setProductsLoading(false);
    }
  }, [page, pageSize, confidenceBand, debouncedSearch, sortBy, sortReversed]);

  // Fetch categories for override selects
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get<CategoryTreeNode[]>("/categories/tree");
      setCategories(res.data);
    } catch (err) {
      notifications.show({ color: "orange", message: getApiErrorMessage(err, "Failed to load categories") });
    }
  }, []);

  useEffect(() => {
    void fetchRunStatus();
    void fetchCategories();
  }, [fetchRunStatus, fetchCategories]);

  useEffect(() => {
    void fetchProducts();
  }, [fetchProducts]);

  // Poll while running
  useEffect(() => {
    if (run?.status === "running" || run?.status === "pending") {
      pollRef.current = setInterval(() => void fetchRunStatus(), 3000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [run?.status, fetchRunStatus]);

  // Refresh products once when run transitions to completed
  const prevRunStatusRef = useRef(run?.status);
  useEffect(() => {
    const prev = prevRunStatusRef.current;
    prevRunStatusRef.current = run?.status;
    if (run?.status === "completed" && prev !== "completed") {
      void fetchProducts();
    }
  }, [run?.status, fetchProducts]);

  // Flatten categories for select options
  const flatCats = useMemo(() => {
    const result: { id: string; name: string; depth: number; parentId: string | null }[] = [];
    function walk(nodes: CategoryTreeNode[], parentId: string | null) {
      for (const n of nodes) {
        result.push({ id: n.id, name: n.name, depth: n.depth, parentId });
        walk(n.children, n.id);
      }
    }
    walk(categories, null);
    return result;
  }, [categories]);

  const l1Options = useMemo(
    () => flatCats.filter((c) => c.depth === 0).map((c) => ({ value: c.id, label: c.name })),
    [flatCats],
  );
  const l2Options = useMemo(
    () =>
      flatCats
        .filter((c) => c.depth === 1 && c.parentId === editL1)
        .map((c) => ({ value: c.id, label: c.name })),
    [flatCats, editL1],
  );
  const l3Options = useMemo(
    () =>
      flatCats
        .filter((c) => c.depth === 2 && c.parentId === editL2)
        .map((c) => ({ value: c.id, label: c.name })),
    [flatCats, editL2],
  );

  async function handleTrigger() {
    setTriggering(true);
    try {
      const res = await api.post<{ runId: string }>("/admin/classify");
      notifications.show({ color: "blue", message: "Classification run started" });
      setRun({
        id: res.data.runId,
        status: "running",
        stats: null,
        errorMessage: null,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to start classification") });
    } finally {
      setTriggering(false);
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    if (selected.size === products.length) setSelected(new Set());
    else setSelected(new Set(products.map((p) => p.id)));
  }

  async function handleBulkApprove() {
    if (selected.size === 0) return;
    setApproving(true);
    try {
      const res = await api.post<{ approvedCount: number }>("/admin/classify/products/approve", {
        product_ids: [...selected],
      });
      const count = res.data.approvedCount;
      const msg = count === selected.size
        ? `Approved ${count} products`
        : `Approved ${count} of ${selected.size} products`;
      notifications.show({ color: "green", message: msg });
      setSelected(new Set());
      await fetchProducts();
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to approve") });
    } finally {
      setApproving(false);
    }
  }

  function openProductDrawer(product: ClassifiedProduct) {
    setSelectedProduct(product);
    // Pre-populate edit fields from current category path
    // Find matching L1/L2/L3 from the category hierarchy
    if (product.categoryId) {
      const cat = flatCats.find((c) => c.id === product.categoryId);
      if (cat) {
        // Walk up the tree to find L1 and L2
        if (cat.depth === 2) {
          setEditL3(cat.id);
          const l2 = flatCats.find((c) => c.id === cat.parentId);
          if (l2) {
            setEditL2(l2.id);
            setEditL1(l2.parentId);
          }
        } else if (cat.depth === 1) {
          setEditL3(null);
          setEditL2(cat.id);
          setEditL1(cat.parentId);
        } else {
          setEditL3(null);
          setEditL2(null);
          setEditL1(cat.id);
        }
      }
    } else {
      setEditL1(null);
      setEditL2(null);
      setEditL3(null);
    }
    setEditLineName(product.productLineName ?? "");
    openDrawer();
  }

  async function handleSaveOverride() {
    if (!selectedProduct) return;
    const categoryId = editL3 ?? editL2 ?? editL1;
    if (!categoryId) {
      notifications.show({ color: "red", message: "Select a category" });
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/classify/products/${selectedProduct.id}/override`, {
        category_id: categoryId,
        product_line_name: editLineName || undefined,
      });
      notifications.show({ color: "green", message: "Classification updated" });
      closeDrawer();
      await fetchProducts();
    } catch (err) {
      notifications.show({ color: "red", message: getApiErrorMessage(err, "Failed to save") });
    } finally {
      setSaving(false);
    }
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <IconSparkles size={24} />
          <Title order={2}>Product Classification</Title>
        </Group>
        <Button
          leftSection={<IconPlayerPlay size={16} />}
          onClick={handleTrigger}
          loading={triggering}
          disabled={run?.status === "running" || run?.status === "pending"}
        >
          Run Classification
        </Button>
      </Group>

      {/* Run Status */}
      {runLoading ? (
        <Group gap="xs"><Loader size="sm" /><Text size="sm">Loading...</Text></Group>
      ) : run ? (
        <>
          {(run.status === "running" || run.status === "pending") && (
            <Alert color="blue" icon={<Loader size={16} />}>
              Classification in progress...
              <Progress value={100} animated mt="xs" />
            </Alert>
          )}
          {run.status === "completed" && run.stats && (
            <Alert color="green" icon={<IconCheck size={16} />}>
              <Group gap="lg">
                <Text size="sm" fw={500}>Run complete</Text>
                <Badge color="green">{run.stats.classified ?? 0} classified</Badge>
                <Badge color="red">{run.stats.failed ?? 0} failed</Badge>
                <Badge color="gray">{run.stats.total ?? 0} total</Badge>
              </Group>
            </Alert>
          )}
          {run.status === "failed" && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              Classification failed: {run.errorMessage ?? "Unknown error"}
            </Alert>
          )}
        </>
      ) : (
        <Alert color="gray">
          No classification runs yet. Click &quot;Run Classification&quot; to begin.
        </Alert>
      )}

      {/* Filters */}
      <Group gap="md">
        <SegmentedControl
          data={[
            { label: "All", value: "all" },
            { label: "Low (<70%)", value: "low" },
            { label: "Med (70-85%)", value: "medium" },
            { label: "High (>85%)", value: "high" },
            { label: "Unclassified", value: "unclassified" },
          ]}
          value={confidenceBand}
          onChange={(v) => { setConfidenceBand(v as ConfidenceBand); setPage(1); }}
        />
        <TextInput
          placeholder="Search products..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
          w={250}
        />
      </Group>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <Group gap="md" p="sm" bg="blue.0" style={{ borderRadius: "var(--mantine-radius-md)" }}>
          <Text size="sm" fw={500}>{selected.size} selected</Text>
          <Button size="xs" leftSection={<IconCheck size={14} />} onClick={handleBulkApprove} loading={approving}>
            Approve Selected
          </Button>
          <Button size="xs" variant="outline" onClick={() => setSelected(new Set())}>
            Clear
          </Button>
        </Group>
      )}

      {/* Products Table */}
      <ScrollArea>
        <Table highlightOnHover verticalSpacing="xs" fz="sm" miw={800}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>
                <Checkbox
                  checked={products.length > 0 && selected.size === products.length}
                  indeterminate={selected.size > 0 && selected.size < products.length}
                  onChange={toggleSelectAll}
                  size="xs"
                />
              </Table.Th>
              <Th sorted={sortBy === "name"} reversed={sortReversed} onSort={() => handleSort("name")}>
                Product
              </Th>
              <Table.Th>Category</Table.Th>
              <Th sorted={sortBy === "classification_confidence"} reversed={sortReversed} onSort={() => handleSort("classification_confidence")}>
                Confidence
              </Th>
              <Table.Th>Method</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {productsLoading ? (
              <Table.Tr>
                <Table.Td colSpan={5} ta="center" py="xl"><Loader size="sm" /></Table.Td>
              </Table.Tr>
            ) : products.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={5} ta="center" py="xl">
                  <Text c="dimmed">No products found</Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              products.map((p) => (
                <Table.Tr
                  key={p.id}
                  onClick={() => openProductDrawer(p)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openProductDrawer(p); } }}
                  tabIndex={0}
                  role="button"
                  style={{ cursor: "pointer" }}
                >
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500} lineClamp={1}>{p.name}</Text>
                    <Group gap={4} mt={2} wrap="wrap">
                      <Badge variant="filled" size="xs" color="dark">{p.brandName}</Badge>
                      {attrBadges(p.attributes).map((attr, idx) => (
                        <Badge key={`${attr}-${idx}`} variant="outline" size="xs" color="gray">{attr}</Badge>
                      ))}
                      {p.productLineName && (
                        <Badge variant="light" size="xs" color="blue">{p.productLineName}</Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c={p.categoryPath ? undefined : "dimmed"}>
                      {formatPath(p.categoryPath)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="center">
                    <Badge
                      color={confidenceColor(p.classificationConfidence)}
                      variant="light"
                      size="sm"
                    >
                      {confidenceLabel(p.classificationConfidence)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="dot"
                      size="xs"
                      color={p.classificationMethod === "manual" ? "green" : p.classificationMethod === "llm" ? "blue" : "gray"}
                    >
                      {p.classificationMethod ?? "—"}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
          </Text>
          <Pagination value={page} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}

      {/* Product Detail / Override Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={selectedProduct ? "Edit Classification" : ""}
        position="right"
        size="md"
        padding="lg"
      >
        {selectedProduct && (
          <Stack gap="md">
            {/* Product info */}
            <div>
              <Text size="lg" fw={600}>{selectedProduct.name}</Text>
              <Group gap={4} mt={4}>
                <Badge variant="filled" size="sm" color="dark">{selectedProduct.brandName}</Badge>
                {selectedProduct.modelNumber && (
                  <Badge variant="outline" size="sm" color="gray">{selectedProduct.modelNumber}</Badge>
                )}
              </Group>
            </div>

            {/* Current classification */}
            <div>
              <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={4}>Current Classification</Text>
              <Text size="sm">{formatPath(selectedProduct.categoryPath)}</Text>
              <Group gap="xs" mt={4}>
                <Badge
                  color={confidenceColor(selectedProduct.classificationConfidence)}
                  variant="light"
                  size="sm"
                >
                  {confidenceLabel(selectedProduct.classificationConfidence)}
                </Badge>
                <Badge variant="dot" size="sm" color={selectedProduct.classificationMethod === "manual" ? "green" : "blue"}>
                  {selectedProduct.classificationMethod ?? "none"}
                </Badge>
              </Group>
            </div>

            {/* Parsed attributes */}
            {selectedProduct.attributes && Object.keys(selectedProduct.attributes).length > 0 && (
              <div>
                <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={4}>Extracted Attributes</Text>
                <Group gap={4} wrap="wrap">
                  {Object.entries(selectedProduct.attributes)
                    .filter(([k]) => !k.startsWith("_"))
                    .map(([key, value]) => (
                      <Badge key={key} variant="outline" size="sm" color="gray">
                        {key}: {String(value)}
                      </Badge>
                    ))}
                </Group>
              </div>
            )}

            {/* Edit category */}
            <div>
              <Text size="xs" fw={500} c="dimmed" tt="uppercase" mb={4}>Assign Category</Text>
              <Stack gap="xs">
                <Select
                  label="L1 Category"
                  placeholder="Select..."
                  data={l1Options}
                  value={editL1}
                  onChange={(v) => { setEditL1(v); setEditL2(null); setEditL3(null); }}
                  clearable
                  searchable
                />
                <Select
                  label="L2 Subcategory"
                  placeholder="Select..."
                  data={l2Options}
                  value={editL2}
                  onChange={(v) => { setEditL2(v); setEditL3(null); }}
                  disabled={!editL1}
                  clearable
                  searchable
                />
                <Select
                  label="L3 Product Type"
                  placeholder="Select..."
                  data={l3Options}
                  value={editL3}
                  onChange={setEditL3}
                  disabled={!editL2}
                  clearable
                  searchable
                />
              </Stack>
            </div>

            {/* Product line */}
            <TextInput
              label="Product Line Name"
              value={editLineName}
              onChange={(e) => setEditLineName(e.currentTarget.value)}
            />

            {/* Actions */}
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeDrawer}>Cancel</Button>
              <Button onClick={handleSaveOverride} loading={saving}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}
